import React, { useState } from 'react';
import PropTypes from 'prop-types';
import HistoryGraph from '../HistoryGraph/HistoryGraph';

const HistoryModal = ({ isOpen, onClose, history, gameName, filterKey, filterValue, onDeletePoint, parTimes }) => {
    const [pointToDelete, setPointToDelete] = useState(null);
    const [runLimit, setRunLimit] = useState('');

    if (!isOpen) return null;

    let filteredHistory = filterKey && filterValue 
        ? history.filter(item => item[filterKey] === filterValue)
        : history;

    if (runLimit && parseInt(runLimit, 10) > 0) {
        filteredHistory = filteredHistory.slice(-parseInt(runLimit, 10));
    }

    const BELT_COLORS = {
        yellow: '#f1c40f',
        green: '#2ecc71',
        blue: '#3498db',
        purple: '#9b59b6',
        brown: '#8B4513',
        black: '#ecf0f1',
    };

    const referenceLines = parTimes ? Object.entries(parTimes).map(([belt, value]) => ({
        value,
        label: belt.charAt(0).toUpperCase() + belt.slice(1),
        color: BELT_COLORS[belt] || '#666'
    })) : [];

    return (
        <div style={modalOverlayStyle}>
            <div style={modalContentStyle}>
                <div style={modalHeaderStyle}>
                    <h2 style={{ margin: 0 }}>{filterValue || gameName} Performance</h2>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <label style={{ fontSize: '12px', color: '#ccc' }}>Last runs:</label>
                        <input 
                            type="number" 
                            value={runLimit} 
                            onChange={(e) => setRunLimit(e.target.value)} 
                            placeholder="All" 
                            style={{ width: '60px', padding: '4px 8px', borderRadius: '4px', border: '1px solid #333', background: '#222', color: '#fff' }} 
                        />
                        <button onClick={onClose} style={closeButtonStyle}>×</button>
                    </div>
                </div>
                
                <div style={modalBodyStyle}>
                    <HistoryGraph 
                        data={filteredHistory} 
                        title={filterValue ? `Total Time Progress: ${filterValue}` : 'Total Time Progress'} 
                        color={gameName.includes('Ninja') ? '#e74c3c' : '#3498db'}
                        dataKey="time"
                        onDeletePoint={setPointToDelete}
                        referenceLines={referenceLines}
                    />

                    {filteredHistory.some(d => d.firstShot) && (
                        <HistoryGraph 
                            data={filteredHistory} 
                            title="First Shot Progress" 
                            color="#f39c12"
                            dataKey="firstShot"
                            binSize={0.1}
                            onDeletePoint={setPointToDelete}
                        />
                    )}

                    {filteredHistory.some(d => d.avgSplit) && (
                        <HistoryGraph 
                            data={filteredHistory} 
                            title={filterValue === '3-Yard Reload' ? "Reload Time Progress" : "Average Split Progress"} 
                            color="#9b59b6"
                            dataKey="avgSplit"
                            binSize={0.05}
                            onDeletePoint={setPointToDelete}
                        />
                    )}

                    {(filterValue === '7-Yard Draw' || filterValue === '25-Yard Shot') && filteredHistory.some(d => d.totalClicks) && (
                        <HistoryGraph 
                            data={filteredHistory} 
                            title="Total Clicks (Including Misses)" 
                            color="#e67e22"
                            dataKey="totalClicks"
                            binSize={1}
                            onDeletePoint={setPointToDelete}
                        />
                    )}
                </div>

                <div style={modalFooterStyle}>
                    <div style={{ display: 'flex', gap: '20px', color: '#888', fontSize: '11px' }}>
                        <div>{filteredHistory.length} sessions</div>
                        {filteredHistory.some(d => d.firstShot) && (
                            <>
                                <div>Best First: {Math.min(...filteredHistory.filter(d => d.firstShot).map(d => d.firstShot)).toFixed(3)}s</div>
                                <div>Best Split: {Math.min(...filteredHistory.filter(d => d.avgSplit).map(d => d.avgSplit)).toFixed(3)}s</div>
                            </>
                        )}
                    </div>
                    <button onClick={onClose} style={closeBtnActionStyle}>
                        Close
                    </button>
                </div>

                {pointToDelete && (
                    <div style={confirmOverlayStyle}>
                        <div style={confirmBoxStyle}>
                            <h3 style={{ marginTop: 0, color: '#e74c3c' }}>Delete Data Point?</h3>
                            <p style={{ color: '#ccc', fontSize: '14px', marginBottom: '20px' }}>
                                Are you sure you want to delete the run from {new Date(pointToDelete.timestamp).toLocaleString()}?
                                <br/><br/>
                                Total Time: {pointToDelete.time?.toFixed(3)}s
                                {pointToDelete.latencyMs > 0 && (
                                    <span style={{ marginLeft: '10px', fontSize: '10px', padding: '2px 6px', background: '#2ecc71', color: '#fff', borderRadius: '10px', textTransform: 'uppercase' }}>
                                        Latency Corrected (−{pointToDelete.latencyMs}ms)
                                    </span>
                                )}
                            </p>
                            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                                <button onClick={() => setPointToDelete(null)} style={{ ...closeBtnActionStyle, background: '#444' }}>
                                    Cancel
                                </button>
                                <button onClick={() => {
                                    if (onDeletePoint) onDeletePoint(pointToDelete);
                                    setPointToDelete(null);
                                }} style={{ ...closeBtnActionStyle, background: '#e74c3c' }}>
                                    Delete
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

const modalOverlayStyle = {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0,0,0,0.85)', zIndex: 1000,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    backdropFilter: 'blur(5px)',
};

const modalContentStyle = {
    position: 'relative',
    background: '#1a1a2e', border: '1px solid #2c3e50', borderRadius: '12px',
    width: '90%', maxWidth: '800px', maxHeight: '80%',
    display: 'flex', flexDirection: 'column',
    boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
    color: '#eee',
};

const modalHeaderStyle = {
    padding: '20px', borderBottom: '1px solid #2c3e50',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
};

const modalBodyStyle = {
    padding: '20px 20px 40px 20px', overflowY: 'auto', flex: 1,
};

const modalFooterStyle = {
    padding: '16px 20px', borderTop: '1px solid #2c3e50',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
};

const closeButtonStyle = {
    background: 'none', border: 'none', color: '#888', fontSize: '28px',
    cursor: 'pointer', padding: '0 10px',
};

const closeBtnActionStyle = {
    background: '#333', color: '#eee', border: 'none',
    padding: '8px 24px', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold',
};

const confirmOverlayStyle = {
    position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0,0,0,0.85)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    borderRadius: '12px', zIndex: 10,
};

const confirmBoxStyle = {
    background: '#2c3e50', padding: '24px', borderRadius: '8px',
    width: '90%', maxWidth: '400px', boxShadow: '0 10px 30px rgba(0,0,0,0.8)',
};

HistoryModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    history: PropTypes.arrayOf(PropTypes.object).isRequired,
    gameName: PropTypes.string.isRequired,
    filterKey: PropTypes.string,
    filterValue: PropTypes.string,
    onDeletePoint: PropTypes.func,
    parTimes: PropTypes.object,
};

export default HistoryModal;
